import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PRODUCTS } from '@tangazo/shared';
import { api } from './client';
import type { AppConfig, MyOrder, Order, User } from './types';

export const keys = {
  config: ['config'] as const,
  me: ['me'] as const,
  order: (token: string) => ['order', token] as const,
  myOrders: ['my-orders'] as const,
};

// The admin sets prices in /admin → Pricing; they replace the built-in ones everywhere PRODUCTS is read.
export const fetchConfig = () => api<AppConfig>('GET', '/api/config').then((c) => {
  for (const p of c.products) if (PRODUCTS[p.id]) PRODUCTS[p.id].price = p.price;
  return c;
});
export const useConfig = () => useQuery({ queryKey: keys.config, queryFn: fetchConfig, staleTime: 5 * 60_000 });
// Pricing v2 feature flags (all off until an admin turns them on).
export const useFlags = () => {
  const c = useConfig();
  return c.data?.flags ?? { pricing_v2: false, wallet_v2: false, script_flow: false, iterations: false, ui_compact: false };
};

export const useMe = () =>
  useQuery({ queryKey: keys.me, queryFn: () => api<{ user: User | null }>('GET', '/api/auth/me').then((r) => r.user), staleTime: 60_000 });

export function useOrder(token: string | undefined, refetchInterval?: number | false) {
  return useQuery({
    queryKey: keys.order(token ?? ''),
    queryFn: () => api<Order>('GET', `/api/orders/${token}`),
    enabled: !!token,
    refetchInterval,
    // Keep checking while the phone shows the mobile-money PIN prompt over the page.
    refetchIntervalInBackground: true,
    retry: (n, err) => {
      const status = (err as { status?: number }).status ?? 0;
      return (status === 0 || status >= 500) && n < 2;
    },
  });
}

export const useMyOrders = (enabled: boolean) =>
  useQuery({ queryKey: keys.myOrders, queryFn: () => api<{ orders: MyOrder[] }>('GET', '/api/me/orders').then((r) => r.orders), enabled });

// Any call that returns the updated order writes it straight into the cache.
export function useOrderMutation<V>(token: string, fn: (v: V) => Promise<Order>) {
  const qc = useQueryClient();
  return useMutation({ mutationFn: fn, onSuccess: (o) => qc.setQueryData(keys.order(token), o) });
}

export const useTemplates = (q: { kind?: string; category?: string; featured?: boolean } = {}) =>
  useQuery({
    queryKey: ['templates', q],
    queryFn: () =>
      api<{ templates: import('./types').TemplateCard[] }>('GET', `/api/templates?${new URLSearchParams(Object.entries(q).filter(([, v]) => v).map(([k, v]) => [k, String(v === true ? 1 : v)]))}`).then((r) => r.templates),
    staleTime: 60_000,
  });

export const useTemplate = (id: string) =>
  useQuery({ queryKey: ['template', id], queryFn: () => api<import('./types').TemplateCard>('GET', `/api/templates/${id}`), retry: false });
